import {
  ErrorBoundary,
  Stack,
  TabsNavigation,
  Text,
} from '@ltvco/refresh-lib/theme';
import { ReportSection } from '@ltvco/refresh-lib/v2';
import { useMemo, useState } from 'react';
import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import { AnswerFormData } from '../CommunityBuzz/QAEntryModal/QAEntryModal';
import { CADisplayModal } from './CADisplayModal/CADisplayModal';
import { ListContent } from './ListContent';
import { StyledBox, StyledTab } from './components';
import { SocialSentiment as SocialSentimentType } from 'api/requests/socialSentiments/interfaces';
import { useSocialSentiment } from 'hooks/useSocialSentiment';
import { RippleLoading } from '@ltvco/refresh-lib/v1';
import { mapCategoryToTab } from './utils';
import type { ResponseError } from '@ltvco/refresh-lib/utils';
import { SOCIAL_SENTIMENT_GA4_EVENTS } from './constants';

interface SocialSentimentProps {
  make: string;
  model: string;
  year: string;
  trim: string;
}

export const SocialSentiment = ({
  make,
  model,
  year,
  trim,
}: SocialSentimentProps) => {
  const { trackEventGA4, logError } = useAppConfigContext();
  const [openQADisplayModal, setOpenQADisplayModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<
    SocialSentimentType | undefined
  >(undefined);

  const {
    getSocialSentimentsQuery,
    getSocialSentimentQuery,
    createCommentMutation,
  } = useSocialSentiment(year, make, model, trim, selectedEntry?.id);

  const {
    data: socialSentimentsData,
    isLoading: isSocialSentimentsLoading,
    isFetching: isSocialSentimentsFetching,
    isError: isSocialSentimentsError,
  } = getSocialSentimentsQuery;

  const { data: socialSentimentDetails } = getSocialSentimentQuery;
  const socialSentiments = socialSentimentsData?.items || [];

  const groupedEntries = useMemo(() => {
    const entriesByCategory = new Map<
      string,
      { label: string; entries: SocialSentimentType[] }
    >();

    socialSentiments.forEach((sentiment) => {
      const tabInfo = mapCategoryToTab(sentiment.category);
      const existing = entriesByCategory.get(tabInfo.key);
      if (existing) {
        existing.entries.push(sentiment);
      } else {
        entriesByCategory.set(tabInfo.key, {
          label: tabInfo.label,
          entries: [sentiment],
        });
      }
    });

    return entriesByCategory;
  }, [socialSentiments]);

  const handleOpenQADisplayModal = () => {
    trackEventGA4({
      event_name:
        SOCIAL_SENTIMENT_GA4_EVENTS.socialSentimentViewSentimentAction.name,
      type: SOCIAL_SENTIMENT_GA4_EVENTS.socialSentimentViewSentimentAction.type,
    });
    setOpenQADisplayModal(true);
  };

  const handleCloseQADisplayModal = () => {
    setOpenQADisplayModal(false);
  };

  const handleSubmitAnswer = async (answerParams: AnswerFormData) => {
    if (!selectedEntry?.id) {
      return;
    }

    try {
      await createCommentMutation.mutateAsync({
        social_sentiment_id: selectedEntry.id,
        body: answerParams.data,
        username: answerParams.name,
      });
    } catch (error) {
      logError('Error submitting comment', error as Error);
      // Re-throw error so QAEntryModal can handle moderation failures (422)
      throw error;
    }
  };

  const tabs = useMemo(
    () =>
      Array.from(groupedEntries.values()).map((group) => ({
        label: group.label,
        content: (
          <ListContent
            entries={group.entries}
            onOpenQADisplayModal={handleOpenQADisplayModal}
            selectedEntry={selectedEntry}
            setSelectedEntry={setSelectedEntry}
          />
        ),
      })),
    [groupedEntries, handleOpenQADisplayModal, selectedEntry, setSelectedEntry],
  );

  const hasContent = Array.from(groupedEntries.values()).some(
    (group) => group.entries.length > 0,
  );
  // Show loading state only while an active fetch/load is in progress.
  const isSocialSentimentsPolling =
    (isSocialSentimentsLoading || isSocialSentimentsFetching) &&
    !isSocialSentimentsError;

  // Always show content when polling to display loading indicator instead of "no content" message
  // This ensures ReportSection shows our children (RippleLoading) instead of default "no content" message
  const hasContentForSection = hasContent || isSocialSentimentsPolling;

  const selectedEntryWithComments = socialSentimentDetails || selectedEntry;

  // Wrapper function to match ErrorBoundary's expected logFunction signature
  const handleErrorBoundaryLog = (
    context: string,
    error: Error | ResponseError | null,
  ) => {
    logError(
      context || 'ErrorBoundary caught error',
      error ?? new Error('Unknown error'),
    );
  };

  return (
    <ErrorBoundary
      trackEventFunction={trackEventGA4}
      logFunction={handleErrorBoundaryLog}
      section="social-sentiment-section"
      fallback={
        <ReportSection
          dataCy="social-sentiment"
          title="Social Sentiment"
          hasContent={false}
          sectionId="social-sentiment-section"
          customNullText="Something went wrong processing the social sentiment section."
          titleSx={{
            fontSize: '1.5rem',
          }}
        />
      }
    >
      <ReportSection
        dataCy="social-sentiment"
        title="Social Sentiment"
        hasContent={hasContentForSection}
        sectionId="social-sentiment-section"
        titleSx={{
          fontSize: '1.5rem',
        }}
      >
        {isSocialSentimentsPolling ? (
          <RippleLoading />
        ) : isSocialSentimentsError ? (
          <Text variant="h4" color="text.secondary" textAlign="center" my={3}>
            Error loading social sentiments. Please try again later.
          </Text>
        ) : (
          <Stack>
            <Text variant="body2" color="text.secondary">
              What are people saying about this car?
            </Text>
            <TabsNavigation tabs={tabs} container={StyledBox} tab={StyledTab} />
          </Stack>
        )}
        <CADisplayModal
          open={openQADisplayModal}
          onClose={handleCloseQADisplayModal}
          question={selectedEntryWithComments}
          onSubmitAnswer={handleSubmitAnswer}
        />
      </ReportSection>
    </ErrorBoundary>
  );
};
